import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { UserProfile, V2AuthContext } from '@shared/types/auth';
import type { DoctorAccountControlRequest } from '@jema-manager/shared';

const AUTH_STORE_KEY = 'jema_auth_store_v2';
const AUTH_STORE_VERSION = 3;
const AUTH_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

type AuthStoreState = {
  user: UserProfile | null;
  authContext: V2AuthContext | null;
  actorUser: UserProfile | null;
  actorAuthContext: V2AuthContext | null;
  effectiveUser: UserProfile | null;
  effectiveAuthContext: V2AuthContext | null;
  isDoctorControlMode: boolean;
  activeDoctorControlRequest: DoctorAccountControlRequest | null;
  capabilities: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  persistedAt: number | null;
  setAuthSnapshot: (payload: {
    user: UserProfile | null;
    authContext: V2AuthContext | null;
    isAuthenticated: boolean;
    isLoading: boolean;
  }) => void;
  setEffectiveAuthSnapshot: (payload: {
    actorUser: UserProfile;
    actorAuthContext: V2AuthContext;
    effectiveUser: UserProfile;
    effectiveAuthContext: V2AuthContext;
    activeDoctorControlRequest: DoctorAccountControlRequest;
    isAuthenticated: boolean;
    isLoading: boolean;
  }) => void;
  setLoading: (isLoading: boolean) => void;
  beginSessionRevalidation: () => void;
  exitDoctorControlMode: () => void;
  clearAuth: () => void;
  validateSnapshot: () => void;
};

const baseState = {
  user: null,
  authContext: null,
  actorUser: null,
  actorAuthContext: null,
  effectiveUser: null,
  effectiveAuthContext: null,
  isDoctorControlMode: false,
  activeDoctorControlRequest: null,
  capabilities: [],
  isAuthenticated: false,
  isLoading: true,
  persistedAt: null,
};

const isSnapshotExpired = (persistedAt: number | null) => {
  if (!persistedAt) return true;
  return Date.now() - persistedAt > AUTH_SNAPSHOT_TTL_MS;
};

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set, get) => {
      return {
        ...baseState,

        setAuthSnapshot: ({ user, authContext, isAuthenticated, isLoading }) => {
          set({
            user,
            authContext,
            actorUser: user,
            actorAuthContext: authContext,
            effectiveUser: user,
            effectiveAuthContext: authContext,
            isDoctorControlMode: false,
            activeDoctorControlRequest: null,
            capabilities: authContext?.capabilities ?? [],
            isAuthenticated,
            isLoading,
            persistedAt: Date.now(),
          });
        },

        setEffectiveAuthSnapshot: ({
          actorUser,
          actorAuthContext,
          effectiveUser,
          effectiveAuthContext,
          activeDoctorControlRequest,
          isAuthenticated,
          isLoading,
        }) => {
          set({
            user: effectiveUser,
            authContext: effectiveAuthContext,
            actorUser,
            actorAuthContext,
            effectiveUser,
            effectiveAuthContext,
            isDoctorControlMode: true,
            activeDoctorControlRequest,
            capabilities: effectiveAuthContext.capabilities,
            isAuthenticated,
            isLoading,
            persistedAt: Date.now(),
          });
        },

        setLoading: isLoading => {
          set(state => ({
            ...state,
            isLoading,
          }));
        },

        beginSessionRevalidation: () => {
          set(state => {
            const hasPersistedIdentity = Boolean(
              state.isAuthenticated || state.user || state.authContext
            );

            if (!hasPersistedIdentity) {
              return state;
            }

            return {
              ...state,
              isLoading: true,
            };
          });
        },

        exitDoctorControlMode: () => {
          set(state => {
            if (!state.actorUser || !state.actorAuthContext) {
              return {
                ...state,
                isDoctorControlMode: false,
                activeDoctorControlRequest: null,
                persistedAt: Date.now(),
              };
            }

            return {
              ...state,
              user: state.actorUser,
              authContext: state.actorAuthContext,
              effectiveUser: state.actorUser,
              effectiveAuthContext: state.actorAuthContext,
              isDoctorControlMode: false,
              activeDoctorControlRequest: null,
              capabilities: state.actorAuthContext.capabilities,
              persistedAt: Date.now(),
            };
          });
        },

        clearAuth: () => {
          set({
            ...baseState,
            isLoading: false,
          });
        },

        validateSnapshot: () => {
          const { persistedAt } = get();
          if (!isSnapshotExpired(persistedAt)) return;

          set({
            ...baseState,
            isLoading: true,
          });
        },
      };
    },
    {
      name: AUTH_STORE_KEY,
      version: AUTH_STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        user: state.user,
        authContext: state.authContext,
        actorUser: state.actorUser,
        actorAuthContext: state.actorAuthContext,
        effectiveUser: state.effectiveUser,
        effectiveAuthContext: state.effectiveAuthContext,
        isDoctorControlMode: state.isDoctorControlMode,
        activeDoctorControlRequest: state.activeDoctorControlRequest,
        capabilities: state.capabilities,
        isAuthenticated: state.isAuthenticated,
        persistedAt: state.persistedAt,
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<AuthStoreState>),
        isLoading: currentState.isLoading,
      }),
      migrate: (persistedState, version) => {
        if (version !== AUTH_STORE_VERSION) {
          return {
            ...baseState,
          };
        }

        return persistedState as Omit<
          AuthStoreState,
          | 'setAuthSnapshot'
          | 'setEffectiveAuthSnapshot'
          | 'setLoading'
          | 'beginSessionRevalidation'
          | 'exitDoctorControlMode'
          | 'clearAuth'
          | 'validateSnapshot'
        >;
      },
      onRehydrateStorage: () => state => {
        state?.validateSnapshot();
        state?.beginSessionRevalidation();
      },
    }
  )
);

export const useAuthUser = () => useAuthStore(state => state.user);
export const useAuthContextSnapshot = () => useAuthStore(state => state.authContext);
export const useAuthLoading = () => useAuthStore(state => state.isLoading);
export const useIsAuthenticated = () => useAuthStore(state => state.isAuthenticated);
